import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Printer, FileText, ShieldCheck, Ban, Fuel, Truck } from 'lucide-react';
import { PageShell, primaryButton, ghostButton, inputStyle, glassLight, T, Badge, StatCard, EmptyState, Field, SectionTitle, usePagination, Pagination, useIsMobile, CampoValidado, useConfirmacion } from '../ui';
import { COMPARTIMIENTOS, DESTINATARIO, PRODUCTOS_WOP, fmtFecha, fmtGal, imprimirPedido, nuevoToken } from './pedidoDocumento';
import type { Pedido, PedidoLinea, ProductoWOP } from '../types';
const hoyISO = () => new Date().toISOString().split('T')[0];
const totalGalones = (p: Pedido) => p.lineas.reduce((sum, l) => sum + (l.galones || 0), 0);
const estadoStyle = (estado: Pedido['estado']) => {
  if (estado === 'autorizado') return {
    color: T.ok,
    soft: T.okSoft,
    label: 'Autorizado'
  };
  if (estado === 'anulado') return {
    color: T.danger,
    soft: T.dangerSoft,
    label: 'Anulado'
  };
  return {
    color: T.warn,
    soft: T.warnSoft,
    label: 'Borrador'
  };
};
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};

// Línea vacía para un compartimiento que todavía no se llenó.
const lineaVacia = (compartimiento: number): PedidoLinea => ({
  compartimiento,
  producto: 'gasoilRegular',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const lineaGLPVacia = (): PedidoLinea => ({
  compartimiento: 1,
  producto: 'glp',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const pedidoVacio = (): Partial<Pedido> => ({
  tipo: 'GLP',
  numeroOrden: '',
  fecha: hoyISO(),
  conductor: '',
  paseChofer: '',
  fichaCola: '',
  placaCamion: '',
  lineas: [lineaGLPVacia()],
  estado: 'borrador',
  observaciones: ''
});
interface PedidosProps {
  pedidos: Pedido[];
  onAdd: (p: Pedido) => void;
  onUpdate: (p: Pedido) => void;
  onDelete: (id: string) => void;
  // Permisos resueltos por el rol del usuario (ver App.tsx).
  canEdit: boolean;
  canArchive: boolean;
  canAutorizar: boolean;
  // Quién firma cuando se autoriza una orden.
  usuarioNombre: string;
  usuarioCargo: string;
  onLog?: (mensaje: string) => void;
}
export function Pedidos({
  pedidos,
  onAdd,
  onUpdate,
  onDelete,
  canEdit,
  canArchive,
  canAutorizar,
  usuarioNombre,
  usuarioCargo,
  onLog
}: PedidosProps) {
  const isMobile = useIsMobile();
  const [dialogo, confirmar] = useConfirmacion();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<'todos' | 'WOP' | 'GLP'>('todos');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'borrador' | 'autorizado' | 'anulado'>('todos');
  const [form, setForm] = useState<Partial<Pedido>>(pedidoVacio());
  const lineas = form.lineas ?? [];

  // ── FILTRADO ──────────────────────────────────────────────────────────
  const filtrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return pedidos.filter(p => filterTipo === 'todos' || p.tipo === filterTipo).filter(p => filterEstado === 'todos' || p.estado === filterEstado).filter(p => {
      if (!q) return true;
      const enDestinos = p.lineas.some(l => l.destino.toLowerCase().includes(q) || l.rncDestino.toLowerCase().includes(q));
      return p.numeroOrden.toLowerCase().includes(q) || p.conductor.toLowerCase().includes(q) || p.placaCamion.toLowerCase().includes(q) || enDestinos;
    }).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [pedidos, searchTerm, filterTipo, filterEstado]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtrados, 6);

  // ── ESTADÍSTICAS ──────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const vivos = pedidos.filter(p => p.estado !== 'anulado');
    const mes = hoyISO().slice(0, 7);
    return {
      total: pedidos.length,
      borradores: pedidos.filter(p => p.estado === 'borrador').length,
      autorizados: pedidos.filter(p => p.estado === 'autorizado').length,
      galonesMes: vivos.filter(p => (p.fecha || '').startsWith(mes)).reduce((s, p) => s + totalGalones(p), 0)
    };
  }, [pedidos]);

  // ── FORMULARIO ────────────────────────────────────────────────────────
  const abrirNuevo = () => {
    setForm(pedidoVacio());
    setEditingId(null);
    setShowForm(true);
  };
  const abrirEdicion = (p: Pedido) => {
    setForm({
      ...p,
      lineas: p.lineas.map(l => ({
        ...l
      }))
    });
    setEditingId(p.id);
    setShowForm(true);
  };
  const cerrarForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(pedidoVacio());
  };

  // Cambiar de formato reinicia el detalle: las líneas de WOP (6
  // compartimientos con producto) y las de GLP (una sola, sin producto) no
  // son intercambiables.
  const cambiarTipo = (tipo: Pedido['tipo']) => {
    setForm(prev => ({
      ...prev,
      tipo,
      lineas: tipo === 'WOP' ? COMPARTIMIENTOS.map(lineaVacia) : [lineaGLPVacia()]
    }));
  };
  const setLinea = (index: number, patch: Partial<PedidoLinea>) => {
    setForm(prev => ({
      ...prev,
      lineas: (prev.lineas ?? []).map((l, i) => i === index ? {
        ...l,
        ...patch
      } : l)
    }));
  };
  // Qué falta para poder guardar. Se calcula en cada render para poder
  // señalar el campo concreto en vez de soltar un alert con una sola queja
  // a la vez.
  const faltantes: Record<string, string> = {};
  if (!form.numeroOrden?.trim()) faltantes.numeroOrden = 'Indica el número de orden';
  if (!form.fecha) faltantes.fecha = 'Indica la fecha del pedido';
  if (!form.conductor?.trim()) faltantes.conductor = 'Indica el conductor';
  if (!form.placaCamion?.trim()) faltantes.placaCamion = 'Indica la placa del camión';
  const lineasUtiles = (form.lineas ?? []).filter(l => (l.galones || 0) > 0);
  if (lineasUtiles.length === 0) {
    faltantes.lineas = form.tipo === 'WOP' ? 'Registra al menos un compartimiento con galones' : 'Indica la cantidad de galones';
  } else if (lineasUtiles.some(l => !l.destino.trim())) {
    faltantes.lineas = 'Cada línea con galones necesita su destino';
  }
  const [mostrarFaltantes, setMostrarFaltantes] = useState(false);
  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    // Los errores solo se destapan al intentar guardar: marcar campos en rojo
    // apenas se abre el formulario es hostil.
    if (Object.keys(faltantes).length) {
      setMostrarFaltantes(true);
      return;
    }
    const base = {
      ...form,
      lineas: lineasUtiles
    } as Pedido;
    const galones = lineasUtiles.reduce((sum, l) => sum + (l.galones || 0), 0);
    const ok = await confirmar({
      titulo: editingId ? '¿Guardar los cambios?' : '¿Crear esta orden de pedido?',
      mensaje: editingId ? 'La orden se actualizará con los datos del formulario.' : 'La orden quedará en borrador hasta que alguien la autorice.',
      detalles: [{
        etiqueta: 'Orden',
        valor: base.numeroOrden
      }, {
        etiqueta: 'Formato',
        valor: base.tipo
      }, {
        etiqueta: 'Conductor',
        valor: base.conductor
      }, {
        etiqueta: 'Placa',
        valor: base.placaCamion
      }, {
        etiqueta: lineasUtiles.length === 1 ? 'Destino' : 'Destinos',
        valor: String(lineasUtiles.length)
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(galones)} galones`
      }],
      confirmar: editingId ? 'Guardar cambios' : 'Crear orden',
      tono: 'accent'
    });
    if (!ok) return;
    if (editingId) {
      onUpdate({
        ...base,
        id: editingId
      });
      onLog?.(`Orden de pedido editada: ${base.numeroOrden} (${base.tipo})`);
    } else {
      onAdd({
        ...base,
        id: Date.now().toString(),
        estado: 'borrador',
        autorizadoPor: '',
        autorizadoCargo: '',
        autorizadoFecha: '',
        // El token se acuña al autorizar, no al crear: un borrador todavía
        // no es un documento verificable.
        token: '',
        fecha_creacion: hoyISO()
      });
      onLog?.(`Orden de pedido creada: ${base.numeroOrden} (${base.tipo})`);
    }
    setMostrarFaltantes(false);
    cerrarForm();
  };

  // ── AUTORIZACIÓN ──────────────────────────────────────────────────────
  // Autorizar equivale a firmar el formulario: se estampa quién lo hizo y
  // desde ahí el pedido ya no se edita.
  const autorizar = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Autorizar esta orden de pedido?',
      mensaje: `Tu nombre quedará estampado en el "AUTORIZADO POR" del formulario y se generará su código QR de verificación.`,
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Formato',
        valor: p.tipo
      }, {
        etiqueta: 'Conductor',
        valor: p.conductor || '—'
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }, {
        etiqueta: 'Autoriza',
        valor: usuarioNombre
      }],
      advertencia: 'Una vez autorizada, la orden ya no se puede editar.',
      confirmar: 'Sí, autorizar',
      tono: 'ok'
    });
    if (!ok) return;
    onUpdate({
      ...p,
      estado: 'autorizado',
      autorizadoPor: usuarioNombre,
      autorizadoCargo: usuarioCargo,
      autorizadoFecha: hoyISO(),
      // Se acuña una sola vez: si la orden ya se autorizó antes (y se
      // reautoriza tras una corrección) el QR impreso sigue siendo válido.
      token: p.token || nuevoToken()
    });
    onLog?.(`Orden de pedido autorizada: ${p.numeroOrden} por ${usuarioNombre}`);
  };
  const anular = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Anular esta orden?',
      mensaje: 'La orden queda registrada pero marcada como anulada. El formulario impreso saldrá con la marca "ANULADO" y quien escanee su QR verá que ya no es válida.',
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }],
      confirmar: 'Sí, anular',
      tono: 'warn'
    });
    if (!ok) return;
    onUpdate({
      ...p,
      estado: 'anulado'
    });
    onLog?.(`Orden de pedido anulada: ${p.numeroOrden}`);
  };
  const eliminar = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Eliminar esta orden?',
      mensaje: 'Se borra del sistema y no queda registro de ella salvo en la auditoría.',
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Estado',
        valor: estadoStyle(p.estado).label
      }],
      advertencia: 'Esta acción no se puede deshacer. Si solo quieres invalidarla, usa Anular.',
      confirmar: 'Sí, eliminar',
      tono: 'danger'
    });
    if (!ok) return;
    onDelete(p.id);
    onLog?.(`Orden de pedido eliminada: ${p.numeroOrden}`);
  };

  // ── IMPRESIÓN ─────────────────────────────────────────────────────────
  const imprimir = (p: Pedido) => {
    imprimirPedido(p);
    onLog?.(`Orden de pedido impresa: ${p.numeroOrden}`);
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return <><PageShell title="Órdenes de Pedido" sub={`Formulario de orden de pedido / retiro bajo contrato — ${DESTINATARIO}`} action={canEdit ? <button onClick={abrirNuevo} style={primaryButton} data-id="element-722" data-mp="3j5r23gwog5ko">
            <Plus size={15} data-id="element-723" data-mp="34g32ck459ez2" /> Nueva orden
          </button> : undefined} data-id="element-721" data-mp="3ee93v0m1gj3e">

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
        gap: 14,
        marginBottom: 26
      }} data-id="element-724" data-mp="34dqc7jo8fyax">

        <StatCard label="Órdenes" value={String(stats.total)} note="registradas" data-id="element-725" data-mp="2vgg05w3lc54y" />
        <StatCard label="Borradores" value={String(stats.borradores)} note="sin autorizar" accent={T.warn} data-id="element-726" data-mp="30ehj8h10o4gf" />

        <StatCard label="Autorizadas" value={String(stats.autorizados)} note="listas para despacho" accent={T.ok} data-id="element-727" data-mp="15z1698fiw9at" />

        <StatCard label="Galones del mes" value={fmtGal(stats.galonesMes)} note="AG solicitados" accent={T.info} data-id="element-728" data-mp="115q1rdy6o4m2" />

      </div>

      {/* ── FILTROS ── */}
      <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        marginBottom: 20
      }} data-id="element-729" data-mp="s74kdm4g6vmv">

        <div style={{
          position: 'relative',
          flex: '1 1 220px'
        }} data-id="element-730" data-mp="1ox3pf6le3c0p">

          <Search size={15} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-731" data-mp="1jxist2tozfr5" />

          <input style={{
            ...inputStyle,
            paddingLeft: 34
          }} placeholder="Buscar por orden, conductor, placa o destino" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-732" data-mp="1axlz2msg3qp8" />

        </div>
        <select style={{
          ...inputStyle,
          flex: '0 1 150px'
        }} value={filterTipo} onChange={e => setFilterTipo(e.target.value as typeof filterTipo)} data-id="element-733" data-mp="3vmifqn899vbe">

          <option value="todos" data-id="element-734" data-mp="2g3khvaxzh4a6">Todos los formatos</option>
          <option value="GLP" data-id="element-735" data-mp="2g1zldfs3zb9o">GLP</option>
          <option value="WOP" data-id="element-736" data-mp="2k5p6fv9krapy">WOP</option>
        </select>
        <select style={{
          ...inputStyle,
          flex: '0 1 160px'
        }} value={filterEstado} onChange={e => setFilterEstado(e.target.value as typeof filterEstado)} data-id="element-737" data-mp="2k44bw5n9frs3">

          <option value="todos" data-id="element-738" data-mp="2tvikr413q3xw">Todos los estados</option>
          <option value="borrador" data-id="element-739" data-mp="2yu25j13yl9oo">Borrador</option>
          <option value="autorizado" data-id="element-740" data-mp="2ywy48rnms2e6">Autorizado</option>
          <option value="anulado" data-id="element-741" data-mp="33uzp9i4mabsa">Anulado</option>
        </select>
      </div>

      {/* ── FORMULARIO ── */}
      <AnimatePresence data-id="element-742" data-mp="37u8i8kru25ms">
        {showForm && <motion.form onSubmit={guardar} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} style={{
          ...glassLight,
          padding: isMobile ? 18 : 24,
          marginBottom: 24,
          overflow: 'hidden'
        }} data-id="element-743" data-mp="37t760cz2zd3h">

            <SectionTitle data-id="element-744" data-mp="e4p7dd4nqf6a">
              {editingId ? 'Editar orden de pedido' : 'Nueva orden de pedido'}
            </SectionTitle>

            <Field label="Formato del formulario" data-id="element-745" data-mp="s3fe7kha3t7u">
              <div style={{
              display: 'flex',
              gap: 10
            }} data-id="element-746" data-mp="n65i5lzdwjb1">

                {(['GLP', 'WOP'] as const).map(t => {
                const activo = form.tipo === t;
                return <button key={t} type="button" onClick={() => cambiarTipo(t)} style={{
                  ...(activo ? primaryButton : ghostButton),
                  flex: 1,
                  justifyContent: 'center'
                }} data-id="element-747" data-mp="3qnx3wimxg70s">

                      {t === 'GLP' ? <Fuel size={15} data-id="element-748" data-mp="4duis23xv3ho" /> : <Truck size={15} data-id="element-749" data-mp="3cvqxvpdhngf2" />}
                      {t === 'GLP' ? 'GLP' : 'GLP'}
                    </button>;
              })}
              </div>
            </Field>

            <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 16
          }} data-id="element-750" data-mp="37ujrnuqzupai">

              <Field label="Orden de pedido número *" data-id="element-751" data-mp="1s5v7wsmnj6mo">
                <CampoValidado modo="numeros" valor={form.numeroOrden ?? ''} onChange={v => setForm({
                ...form,
                numeroOrden: v
              })} placeholder="1587725" maxLength={15} data-id="element-752" data-mp="1s7et8xn607vb" />

              </Field>
              <Field label="Fecha *" data-id="element-753" data-mp="264l8ii8ojofs">
                <input type="date" style={inputStyle} value={form.fecha ?? ''} onChange={e => setForm({
                ...form,
                fecha: e.target.value
              })} data-id="element-754" data-mp="265mkcoxiseyk" />

              </Field>
              <Field label="Conductor del camión *" data-id="element-755" data-mp="1x3wrdenzp7yo">
                <CampoValidado modo="letras" valor={form.conductor ?? ''} onChange={v => setForm({
                ...form,
                conductor: v
              })} placeholder="Nombre completo" maxLength={60} data-id="element-756" data-mp="222zzx936hwbe" />

              </Field>
              <Field label="Pase del chofer" data-id="element-757" data-mp="qp4c7by7kf8k">
                <CampoValidado modo="codigo" valor={form.paseChofer ?? ''} onChange={v => setForm({
                ...form,
                paseChofer: v
              })} placeholder="APP-87" maxLength={15} data-id="element-758" data-mp="2sdii19dmsfqz" />

              </Field>
              <Field label="Ficha de la cola" data-id="element-759" data-mp="2imxbmzrpu6xi">
                <CampoValidado modo="codigo" valor={form.fichaCola ?? ''} onChange={v => setForm({
                ...form,
                fichaCola: v
              })} placeholder="33-21" maxLength={15} data-id="element-760" data-mp="2ilm72xqg4v5c" />

              </Field>
              <Field label="Placa del camión *" data-id="element-761" data-mp="24o7g3okjofb9">
                <CampoValidado modo="placa" valor={form.placaCamion ?? ''} onChange={v => setForm({
                ...form,
                placaCamion: v
              })} placeholder="L-317355" data-id="element-762" data-mp="24mcu06xjeh5l" />

              </Field>
            </div>

            <SectionTitle data-id="element-763" data-mp="1vplx5sb5uy9y">
              {form.tipo === 'WOP' ? 'Detalle por compartimiento' : 'Detalle del despacho'}
            </SectionTitle>

            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginBottom: 18
          }} data-id="element-764" data-mp="3q2bw4ogdyxo7">

              {lineas.map((linea, i) => <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : form.tipo === 'WOP' ? '96px 1.3fr 100px 1.4fr 1fr' : '120px 1.6fr 1fr',
              gap: 10,
              alignItems: 'center',
              padding: '10px 12px',
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.rMd
            }} data-id="element-765" data-mp="1gs2owu4n4drj" data-mpi={i}>

                  {form.tipo === 'WOP' && <span style={{
                fontSize: 12,
                fontWeight: 700,
                color: T.text
              }} data-id="element-766" data-mp="xujfhpe9st4l">

                      Compart. {linea.compartimiento}
                    </span>}
                  {form.tipo === 'WOP' && <select style={inputStyle} value={linea.producto} onChange={e => setLinea(i, {
                producto: e.target.value as ProductoWOP
              })} data-id="element-767" data-mp="16s25z9aonznv">

                      {PRODUCTOS_WOP.map(prod => <option key={prod.id} value={prod.id} data-id="element-768" data-mp="1tx18eeza9vjq">
                          {prod.label}
                        </option>)}
                    </select>}
                  <CampoValidado modo="numeros" valor={linea.galones ? String(linea.galones) : ''} onChange={v => setLinea(i, {
                galones: Number(v) || 0
              })} placeholder="Galones" maxLength={7} data-id="element-769" data-mp="1ozhvpji8hqtw" />

                  <CampoValidado valor={linea.destino} onChange={v => setLinea(i, {
                destino: v
              })} placeholder="Destino" maxLength={60} data-id="element-770" data-mp="1xws672n89bxr" />

                  <CampoValidado modo="rnc" valor={linea.rncDestino} onChange={v => setLinea(i, {
                rncDestino: v
              })} placeholder="RNC del destino" data-id="element-771" data-mp="27nmwci3lgvoh" />

                </div>)}
            </div>

            {form.tipo === 'WOP' && <div style={{
            fontSize: 11,
            color: T.textMuted,
            marginBottom: 16,
            marginTop: -8
          }} data-id="element-772" data-mp="2hkrem6ubk700">

                Los compartimientos sin galones no se imprimen en el formulario.
              </div>}

            <Field label="Observaciones" data-id="element-773" data-mp="d5pdts3895vy">
              <input style={inputStyle} value={form.observaciones ?? ''} onChange={e => setForm({
              ...form,
              observaciones: e.target.value
            })} placeholder="Opcional" data-id="element-774" data-mp="d70evk5afzjd" />

            </Field>

            {mostrarFaltantes && Object.keys(faltantes).length ? <div style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 12,
            background: T.dangerSoft,
            border: `1px solid ${T.danger}33`,
            color: T.danger,
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.6
          }} data-id="element-775" data-mp="8ccvu0t1zxc6">
              Falta completar: {Object.values(faltantes).join(' · ')}
            </div> : null}
            <div style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end'
          }} data-id="element-776" data-mp="3lkpqpetwc7z3">

              <button type="button" onClick={cerrarForm} style={ghostButton} data-id="element-777" data-mp="1jt2xd6mj80zg">
                Cancelar
              </button>
              <button type="submit" style={primaryButton} data-id="element-778" data-mp="2nczfg1bar5wl">
                {editingId ? 'Guardar cambios' : 'Crear orden'}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>

      {/* ── LISTADO ── */}
      {filtrados.length === 0 ? <EmptyState title="Sin órdenes de pedido" hint={canEdit ? 'Crea la primera orden para imprimir el formulario de Refidomsa.' : 'Tu rol no permite crear órdenes de pedido.'} icon={<FileText size={22} data-id="element-780" data-mp="364jue0iibfrj" />} data-id="element-779" data-mp="2dd8lc0qnesx1" /> : <>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }} data-id="element-781" data-mp="3b2lbwh0ab70w">

            {pageItems.map(p => {
            const es = estadoStyle(p.estado);
            const bloqueado = p.estado !== 'borrador';
            return <motion.div key={p.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} style={{
              ...glassLight,
              padding: isMobile ? 16 : '18px 20px',
              display: 'flex',
              gap: 16,
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap'
            }} data-id="element-782" data-mp="3132ua77ujxc0">

                  <div style={{
                flex: '1 1 260px',
                minWidth: 0
              }} data-id="element-783" data-mp="1s4fd3cromh3y">

                    <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginBottom: 6
                }} data-id="element-784" data-mp="1n643921e06lq">

                      <span style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: T.text
                  }} data-id="element-785" data-mp="uhq1cb76gq4w">

                        Orden {p.numeroOrden || '—'}
                      </span>
                      <Badge label={p.tipo} color={p.tipo === 'GLP' ? T.brand : T.info} soft={p.tipo === 'GLP' ? T.accentSoft : T.infoSoft} dot={false} data-id="element-786" data-mp="14cqdrcgg0sdi" />

                      <Badge label={es.label} color={es.color} soft={es.soft} data-id="element-787" data-mp="bp4bc7iybts8" />
                    </div>
                    <div style={{
                  fontSize: 12,
                  color: T.textSecondary
                }} data-id="element-788" data-mp="11d9oiydmay2u">

                      {fmtFecha(p.fecha)} · {p.conductor || 'Sin conductor'} ·{' '}
                      {p.placaCamion || 'Sin placa'}
                    </div>
                    <div style={{
                  fontSize: 12,
                  color: T.textMuted,
                  marginTop: 3
                }} data-id="element-789" data-mp="1f4el1rlova8y">

                      {p.lineas.length}{' '}
                      {p.lineas.length === 1 ? 'destino' : 'destinos'} ·{' '}
                      {fmtGal(totalGalones(p))} galones
                      {p.estado === 'autorizado' && p.autorizadoPor ? ` · Autorizó ${p.autorizadoPor}` : ''}
                    </div>
                  </div>

                  <div style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap'
              }} data-id="element-790" data-mp="22vtr9vgl6bph">

                    <button type="button" onClick={() => imprimir(p)} style={iconBtn} title="Imprimir formulario" data-id="element-791" data-mp="3sc62gyhhjbes">

                      <Printer size={14} data-id="element-792" data-mp="3ncjebu04car7" />
                    </button>

                    {canAutorizar && p.estado === 'borrador' && <button type="button" onClick={() => autorizar(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.ok}55`,
                  color: T.ok
                }} title="Autorizar orden" data-id="element-793" data-mp="3ilkyswmyfgp0">

                        <ShieldCheck size={14} data-id="element-794" data-mp="39n98ywtwwmxm" />
                      </button>}

                    {canEdit && !bloqueado && <button type="button" onClick={() => abrirEdicion(p)} style={iconBtn} title="Editar" data-id="element-795" data-mp="34l0flzd4niuz">

                        <Edit2 size={14} data-id="element-796" data-mp="2zpl0l43dvgwz" />
                      </button>}

                    {canArchive && p.estado === 'autorizado' && <button type="button" onClick={() => anular(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.warn}55`,
                  color: T.warn
                }} title="Anular orden" data-id="element-797" data-mp="2uqruvxuc8u7d">

                        <Ban size={14} data-id="element-798" data-mp="10x71g4ze5zrf" />
                      </button>}

                    {canArchive && p.estado !== 'autorizado' && <button type="button" onClick={() => eliminar(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.danger}55`,
                  color: T.danger
                }} title="Eliminar" data-id="element-799" data-mp="15x3708nqlz93">

                        <Trash2 size={14} data-id="element-800" data-mp="vx2hu636sowz" />
                      </button>}
                  </div>

                </motion.div>;
          })}
          </div>

          <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="órdenes" data-id="element-801" data-mp="mxp8ddf2c0c9" />

        </>}
    </PageShell>
    {dialogo}
  </>;
}